/**
 * Centralized API headers utility
 * Single source of truth for tenant and auth headers
 */

import { buildAuthFetchHeaders } from './authSession';
import { getAccessToken } from './token';

const TENANT_STORAGE_KEYS = [
  'X_TENANT_ID',
  'x-tenant-id',
  'activeTenantId',
  'tenant_id',
  'activeSchoolId',
  'school_id',
] as const;

/**
 * Get tenant ID from localStorage (canonical gateway header source).
 * Tries several keys so DevTeam / legacy flows still resolve after login or school pickers.
 */
export const getTenantId = (): string => {
  for (const key of TENANT_STORAGE_KEYS) {
    const raw = localStorage.getItem(key)?.trim();
    if (raw) {
      if (key !== 'X_TENANT_ID') {
        try {
          localStorage.setItem('X_TENANT_ID', raw);
        } catch {
          /* ignore */
        }
      }
      return raw;
    }
  }
  if (getAccessToken()) {
    console.warn('Tenant id not found in localStorage (expected X_TENANT_ID or fallbacks)');
  }
  return '';
};

function headersInitToRecord(headers: HeadersInit): Record<string, string> {
  if (headers instanceof Headers) {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    const out: Record<string, string> = {};
    for (const [key, value] of headers) {
      if (value != null) {
        out[key] = value;
      }
    }
    return out;
  }
  return { ...headers };
}

/** Persist active tenant for `x-tenant-id` on gateway + tutor API calls. */
export function persistTenantIdForApi(tenantId: string | null | undefined): void {
  const t = tenantId?.trim();
  try {
    if (t) localStorage.setItem('X_TENANT_ID', t);
    else localStorage.removeItem('X_TENANT_ID');
  } catch {
    /* ignore */
  }
}

/** Clear gateway identity headers stored after a successful /me (also clear on invalid session). */
export function clearIdentityHeaders(): void {
  try {
    localStorage.removeItem('X_ROLE');
    localStorage.removeItem('X_TENANT_ID');
    localStorage.removeItem('X_USER_ID');
  } catch {
    /* ignore */
  }
}

/**
 * Get standard auth headers for API requests
 * @returns Headers object with Authorization, x-tenant-id, x-user-id, x-role, and Accept
 */
export const getAuthHeaders = (): Record<string, string> => {
  return headersInitToRecord(buildAuthFetchHeaders());
};

/**
 * Get tenant ID fallbacks for lessons API
 * First tries the main tenant ID, then falls back to "tkm" for backward compatibility
 * @returns Array of tenant IDs to try in order
 */
export const getLessonTenantIdFallbacks = (): string[] => {
  return [getTenantId(), 'tkm'];
};
